<template>
  <div class="dashboard">
    <div class="dashboard__header">
      <MainHeader />
    </div>
    <div class="dashboard__exchanges-connect">
      <ExchangesConnect v-if="!isBinanceKeyConnected" />
      <ExchangesOverview
          v-else-if="getAssetOverview"
          :asset-overview="getAssetOverview"
          :assets="assets"
          :total-holdings="getTotalHoldings"
          @force-refresh="refreshAccountOverviewData(true)"
          @trade-force-refresh="tradeAccountHandler"
      />
    </div>

    <template v-if="assets && assets.length">
      <div :key="JSON.stringify(assets)" class="dashboard__exchanges-grid">
        <ExchangesGrid label="Protos Trend Strategy" :disable-switch="false" :assets="assets" />
      </div>
      <div class="dashboard__exchanges-chart">
        <PieChart :data-set="getChartData" title="Current Exposure" />
      </div>
    </template>

    <div v-if="assets && assets.length" class="dashboard__exchanges-grid">
      <ExchangesGrid label="Protos Market Strategy (Coming soon)" />
      <ExchangesGrid label="Protos Small Cap Strategy (Coming Soon)" />
      <ExchangesGrid label="Protos Low Vol Strategy (Coming Soon)" />
      <ExchangesGrid label="Protos Value Strategy (Coming Soon)" />
    </div>

    <div  v-if="assets && assets.length" class="dashboard__footer">
      Lean more about strategies on <a href="https://www.protosterminal.com/" target="_blank"> protosterminal.com </a>
      <br />
      Follow Protos Strategy Performance via
      <a href="https://www.protosterminal.com/indices/" target="_blank">protosterminal.com/indices/</a>
      <br />
      <br />
      <br />
    </div>

  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { AUTO_REFRESH_MS } from "@/constants/apis";
import { authGuardRoute } from "@/guards";
import { getAccountOverview, getBinanceKeys, tradeAccount } from "@/services/account";
import MainHeader from "@/components/ui/molecules/MainHeader";
import ExchangesConnect from "@/components/dashboard/ExchangesConnect";
import ExchangesOverview from "@/components/dashboard/ExchangesOverview";
import ExchangesGrid from '@/components/dashboard/ExchangesGrid.vue';
import PieChart from "@/components/ui/molecules/PieChart";
import userMixin from '@/mixins/user'

export default {
  name: 'DashboardView',
  components:  { MainHeader, ExchangesConnect, ExchangesOverview, ExchangesGrid, PieChart },
  beforeRouteEnter: authGuardRoute,
  mixins: [userMixin],
  data() {
    return {
      setIntervalRef: null
    }
  },
  created() {
    this.refreshAccountOverviewData()
    this.getBinanceKeys()

    this.setIntervalRef = setInterval(() => {
      this.getBinanceKeys()
          .then(() => this.refreshAccountOverviewData(true))

    }, AUTO_REFRESH_MS)
  },
  beforeDestroy() {
    clearInterval(this.setIntervalRef)
  },
  watch: {
    isBinanceKeyConnected: {
      immediate: true,
      handler(value) {
        if (value) {
          this.setIsLoading(true)
          Promise.all([
            this.refreshAccountOverviewData(),
            this.getBinanceKeys()
          ]).finally(() => this.setIsLoading(false))
        }
      }
    }
  },
  computed: {
    ...mapGetters({
      getIsLoading: 'getIsLoading',
      isBinanceKeyConnected: 'isBinanceKeyConnected',
      assets: 'getAssets',
      getTotalHoldings: 'getTotalHoldings'
    }),
    getAssetOverview() {
      const bUsdAsset = this.assets.find(({ asset }) => asset === 'USDT')
      return bUsdAsset ?? null
    },
    getChartData() {
      const sumAmounts = this.assets.reduce((a, b) => a + b.amount_value.value, 0)
      return {
        data: this.assets.map(({ amount_value: { value } }) => value/sumAmounts),
        labels: this.assets.map(({ asset }) => asset)
      }
    }
  },
  methods: {
    ...mapActions({
      setIsLoading: 'setIsLoading',
      setSnackbarError: 'setSnackbarError',
      setAssets: 'setAssets',
      setTotalHoldings: 'setTotalHoldings',
      setBinanceKeys: 'setBinanceKeys'
    }),
    refreshAccountOverviewData(forceRefresh = false) {
      if (forceRefresh || !this.assets?.length) {
        this.setIsLoading(true)
        getAccountOverview()
            .then(({ tickers, actual_holdings }) => {
              this.setAssets(tickers)
              this.setTotalHoldings(actual_holdings)
            })
            .catch(({ message }) => {
              this.setSnackbarError(message)
              this.setAssets([])
            })
            .finally(() => this.setIsLoading(false))
      }
    },
    tradeAccountHandler() {
      this.setIsLoading(true)
      tradeAccount()
          .catch(console.error)
          .finally(() => this.setIsLoading(false))
    },
    getBinanceKeys() {
      getBinanceKeys()
          .then(data => {
            const allActiveKeys = data.filter(({ status }) => status)

            const selectedActiveKey = allActiveKeys.length ?
                allActiveKeys[allActiveKeys.length - 1 ] :
                data[data.length - 1]

            this.setBinanceKeys(selectedActiveKey)
          })
          .catch(({ message }) => {
            this.setBinanceKeys({})
            this.setSnackbarError(message)
          })
    }
  }
}
</script>

<style lang="scss" scoped>
.dashboard {

  &__header {
    position: fixed;
    right: 0;
  }

  &__footer {
    color: #868e96;
    font-size: 1rem;
    text-align: left;

    a {
      color: #868e96;
      text-decoration: none;
    }
  }

  &__exchanges-connect, &__exchanges-grid, &__exchanges-holding, &__exchanges-chart, &__footer {
    max-width: 55rem;
    margin: 0 auto;
    padding: 1.25rem 3rem;
  }

  ::v-deep .exchanges-grid {
    margin-bottom: 2rem;
  }

  &__exchanges-connect {
    padding-top: 8.5rem;
  }

  &__exchanges-chart {
    background: white;
    padding: 0;
  }

}

// only mobile
@media (max-width: 768px) {
  .dashboard {

    &__exchanges-connect {
      padding: 8.5rem 1rem 1.25rem 1rem;
    }

    &__exchanges-grid, &__exchanges-holding, &__footer {
      padding: 1.25rem 0.25rem;
    }
  }
}
</style>
